'use client';

import { ArrowRight } from 'lucide-react';
import { buildToolLink, getToolDisplayName, type ToolKey } from '@/lib/tools/tool-links';
import type { URLStateObject } from '@/lib/tools/url-state';

export interface ChainButtonProps {
  /** Target tool to navigate to */
  targetTool: ToolKey;
  /** State/data to pass to the target tool */
  state?: URLStateObject;
  /** Source tool slug (for breadcrumb tracking) */
  sourceTool: string;
  /** Override the default label */
  label?: string;
  /** Button size */
  size?: 'sm' | 'md';
  /** Button variant */
  variant?: 'primary' | 'secondary' | 'ghost';
  /** Additional CSS classes */
  className?: string;
}

/**
 * Button component for chaining to another tool with state transfer.
 * Uses regular <a> tag for R2-bundle compatibility.
 */
export default function ChainButton({
  targetTool,
  state,
  sourceTool,
  label,
  size = 'sm',
  variant = 'secondary',
  className = '',
}: ChainButtonProps) {
  const href = buildToolLink(targetTool, state, { source: sourceTool });
  const displayName = getToolDisplayName(targetTool);
  const buttonLabel = label || displayName;

  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1.5 text-sm',
  };

  const variantClasses = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700',
    secondary: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600',
    ghost: 'text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20',
  };

  return (
    <a
      href={href}
      className={`
        inline-flex items-center gap-1 rounded font-medium transition-colors
        ${sizeClasses[size]}
        ${variantClasses[variant]}
        ${className}
      `}
      title={`Open in ${displayName}`}
    >
      <span>{buttonLabel}</span>
      <ArrowRight className={size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} />
    </a>
  );
}
